import React from "react";
import * as d3 from "d3";
import * as legend from "d3-svg-legend";
import PropTypes from "prop-types";

import "./ScatterPlot.css";

//TODO: need to switch away from these type of global vars
let c_xScale = () => {};
let c_yScale = () => {};
let c_getX = () => {};
let c_getY = () => {};
let c_xAxis = () => {};
let c_yAxis = () => {};
let c_rScale = () => {};
//let categoryScale;

class ScatterPlot extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      scope: {
        xAverage: null,
        yAverage: null,
        xMax: null,
        yMax: null,
        rMax: null
      }
    };

    this.zoom = this.zoom.bind(this);
    this.createVisualization = this.createVisualization.bind(this);
    this.transform = this.transform.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
  }

  getPageCategoryColor(datum) {
    const uncategorizedColor = "grey";

    if (this.props.filters) {
      const filters = this.props.filters;
      if (!datum.path) {
        console.warn("no path was found on data: " + JSON.stringify(datum));
        return uncategorizedColor;
      }

      const path = datum.path;
      let matchedColor;
      filters.forEach(f => {
        if (path.match(f.Pattern)) {
          matchedColor = f.Color;
        }
      });

      return matchedColor ? matchedColor : uncategorizedColor;
    }

    return uncategorizedColor;
  }

  render() {
    if (!this.props.filters) {
      return null;
    }

    return (
      <div id={this.props.id}>
        <svg
          id="matrix-svg"
          width={this.props.width}
          height={this.props.height}
        />
        <div className="tooltip" style={{ opacity: 0 }} />
      </div>
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.pageMetrics && nextProps.pageMetrics === null) {
      d3.select(`#${this.props.id}`)
        .selectAll(".dot")
        .attr("opacity", 1);
      d3.select(`#${this.props.id}`)
        .selectAll("#crosshairs")
        .remove();
    }
  }

  onDotHover(data, params) {
    //show page details for the dot
    const { onDotHover } = this.props;
    if (onDotHover) {
      onDotHover(data, params);
    }
  }

  onDotClick(data, params) {
    //show page details for the dot
    const { onDotSelected } = this.props;
    if (onDotSelected) {
      onDotSelected(data);
    }

    const pageMetricsId = this.props.pageMetrics.id;
    d3.select(`#${this.props.id}`)
      .selectAll(".dot")
      .attr("opacity", d => (d.id !== pageMetricsId ? 0.5 : 1));

    d3.select(`#${this.props.id}`)
      .select("svg.objects")
      .append("circle")
      .attr("id", "crosshairs")
      .attr("r", params.r * 2)
      .attr("transform", params.transform)
      .attr("stroke", "black")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", 5)
      .style("fill", "none");
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextProps.width !== this.props.width ||
      nextProps.height !== this.props.height ||
      nextProps.id !== this.props.id ||
      nextProps.data.length !== this.props.data.length || 
      nextProps.filters !== this.props.filters
    );
  }

  componentDidUpdate() {
    this.createVisualization();
  }

  componentDidMount() {
    this.createVisualization();
  }

  zoom() {
    let svg = d3.select(`#${this.props.id}`).select("svg");

    svg.select(".x.axis").call(c_xAxis);
    svg.select(".y.axis").call(c_yAxis);

    svg
      .selectAll(".x_avg_ref")
      .attr("x1", d => c_xScale(d.x1))
      .attr("y1", d => d.y1)
      .attr("x2", d => c_xScale(d.x2))
      .attr("y2", d => d.y2);

    svg
      .selectAll(".y_avg_ref")
      .attr("x1", d => d.x1)
      .attr("y1", d => c_yScale(d.y1))
      .attr("x2", d => d.x2)
      .attr("y2", d => c_yScale(d.y2));

    svg.selectAll(".dot").attr("transform", this.transform);
  }

  transform(d) {
    let t =
      "translate(" + c_xScale(c_getX(d)) + "," + c_yScale(c_getY(d)) + ")";
    //console.log(t);
    return t;
  }

  createVisualization() {
    const {
      id,
      width,
      height,
      data,
      getX,
      getY,
      getRadius,
      getKey,
      getName,
      getCategoryName,
      xLabel,
      yLabel,
      onScopeChanged
    } = this.props;

    // Chart dimensions.
    c_getX = getX;
    c_getY = getY;

    let margin = { top: 5, right: 5, bottom: 30, left: 50 };
    let outerWidth = width;
    let outerHeight = height;
    let chartWidth = outerWidth - margin.left - margin.right;
    let chartHeight = outerHeight - margin.top - margin.bottom;

    let xMax = d3.max(data, d => getX(d));
    let xMin = d3.min(data, d => getX(d));
    let xAvg = d3.mean(data, d => getX(d));

    let yMax = d3.max(data, d => getY(d));
    let yMin = d3.min(data, d => getY(d));
    let yAvg = d3.mean(data, d => getY(d));

    let rMax = d3.max(data, d => getRadius(d)) * 1.05;

    c_xScale = d3.scale
      .linear()
      .domain([xMin, xMax])
      .range([0, chartWidth])
      .nice();

    c_yScale = d3.scale
      .linear()
      .domain([yMin, yMax])
      .range([chartHeight, 0])
      .nice();

    c_rScale = d3.scale
      .sqrt()
      .domain([0, rMax])
      .range([5, 30])
      .nice();

    // const categories = d3.map(data, d => getCategoryName(d)).keys();

    // categoryScale = d3.scale
    //   .ordinal()
    //   .domain(categories)
    //   .range(d3.scale.category20().range());

    c_xAxis = d3.svg
      .axis()
      .scale(c_xScale)
      .orient("bottom")
      .tickSize(-chartHeight);

    c_yAxis = d3.svg
      .axis()
      .scale(c_yScale)
      .orient("left")
      .tickSize(-chartWidth)
      .ticks(5, "s");

    let color = d3.scale.category20();

    let zoomzoom = d3.behavior
      .zoom()
      .x(c_xScale)
      .y(c_yScale)
      .scaleExtent([0, 500])
      .scale(0.9)
      .on("zoom", this.zoom);

    // Create the SVG container and set the origin.
    let rootSvg = d3
      .select("#matrix-svg")
      .selectAll("g")
      .remove();

    let svg = d3
      .select("#matrix-svg")

      .append("g")

      .attr("transform", "translate(" + margin.left + "," + margin.top + ")")
      .call(zoomzoom);

    svg
      .append("rect")
      .attr("width", chartWidth)
      .attr("height", chartHeight)
      .style("shape-rendering", "crispEdges")
      .style("fill", "white");

    svg
      .append("g")
      .classed("x axis", true)
      .attr("transform", "translate(0," + chartHeight + ")")
      .call(c_xAxis)
      .append("text")
      .classed("axis_label", true)
      .attr("x", 10)
      .attr("y", margin.bottom - 5)
      .style("text-anchor", "end")
      .style("font-size", "16px")
      .attr("font-weight", "bold")
      .text(xLabel);

    svg
      .append("g")
      .classed("y axis", true)
      .call(c_yAxis)
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -chartHeight + margin.bottom + 5)
      .attr("y", -margin.left + 8)
      .attr("dy", ".71em")
      .style("text-anchor", "end")
      .style("font-size", "16px")
      .attr("font-weight", "bold")
      .text(yLabel);

    let objects = svg
      .append("svg")
      .classed("objects", true)
      .attr("width", chartWidth)
      .attr("height", chartHeight);

    objects
      .append("svg:line")
      .classed("axisLine hAxisLine", true)
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", chartWidth)
      .attr("y2", 0)
      .attr("transform", "translate(0," + chartHeight + ")");

    objects
      .append("svg:line")
      .classed("axisLine vAxisLine", true)
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", 0)
      .attr("y2", chartHeight);

    //add a vertical line indicating the average of X-axis
    objects
      .selectAll(".x_avg_ref")
      .data([{ x1: xAvg, y1: 0, x2: xAvg, y2: chartHeight }])
      .enter()
      .append("svg:line")
      .classed("x_avg_ref", true)
      .attr("x1", d => c_xScale(d.x1))
      .attr("y1", d => d.y1)
      .attr("x2", d => c_xScale(d.x2))
      .attr("y2", d => d.y2)
      .style("stroke-width", "2")
      .style("stroke", "black")
      .attr("stroke-dasharray", "2,2");

    //add a vertical line indicating the average of X-axis
    objects
      .selectAll(".y_avg_ref")
      .data([{ x1: 0, y1: yAvg, x2: chartWidth, y2: yAvg }])
      .enter()
      .append("svg:line")
      .classed("y_avg_ref", true)
      .attr("x1", d => d.x1)
      .attr("y1", d => c_yScale(d.y1))
      .attr("x2", d => d.x2)
      .attr("y2", d => c_yScale(d.y2))
      .style("stroke-width", "2")
      .style("stroke", "black")
      .attr("stroke-dasharray", "2,2");

    let self = this;

    const div = d3.select("div.tooltip");

    const grads = objects
      .append("defs")
      .selectAll("radialGradient")
      .data(data)
      .enter()
      .append("radialGradient")
      .attr("gradientUnits", "objectBoundingBox")
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("r", "100%")
      .attr("id", (d, i) => `grad-${i}`);

    grads
      .append("stop")
      .attr("offset", "0%")
      .style("stop-color", "white");

    grads
      .append("stop")
      .attr("offset", "100%")
      .style("stop-color", d => this.getPageCategoryColor(d));

    objects
      .selectAll(".dot")
      .data(data)
      .enter()
      .append("circle")
      .classed("dot", true)
      .attr("r", d => c_rScale(getRadius(d)))
      .attr("transform", this.transform)
      //.style("fill", d => categoryScale(getCategoryName(d)))
      //.style("fill", d => this.getPageCategoryColor(d))
      .style("fill", (d, i) => `url(#grad-${i}`)
      .on("click", function(d, i) {
        self.onDotClick(d, {
          r: c_rScale(getRadius(d)),
          transform: self.transform(d)
        });
      })
      .on("mouseover", function(d, i) {
        self.onDotHover({
          data: d,
          params: {
            transform: this.getBoundingClientRect(),
            fill: `url(#grad-${i}`
          }
        });
      })
      .on("mouseout", () => this.onDotHover(null, null));

    let legend = svg
      .selectAll(".legend")
      .data(color.domain())
      .enter()
      .append("g")
      .classed("legend", true)
      .attr("transform", function(d, i) {
        return "translate(0," + i * 20 + ")";
      });

    legend
      .append("circle")
      .attr("r", 3.5)
      .attr("cx", chartWidth + 20)
      .attr("fill", color);

    legend
      .append("text")
      .attr("x", chartWidth + 26)
      .attr("dy", ".35em")
      .text(d => d);

    let scope = {
      xAverage: xAvg,
      yAverage: yAvg,
      xMax: xMax,
      yMax: yMax,
      rMax: rMax
    };

    //bubble up the details of this chart.
    if (onScopeChanged) {
      onScopeChanged(scope);
    }

    svg
      .append("g")
      .attr("class", "legendOrdinal")
      .attr("transform", "translate(20,20)");

    // const legendOrdinal = d3.legend
    //   .color()
    //   //d3 symbol creates a path-string, for example
    //   //"M0,-8.059274488676564L9.306048591020996,
    //   //8.059274488676564 -9.306048591020996,8.059274488676564Z"
    //   .shape(
    //     "path",
    //     d3.svg
    //       .symbol()
    //       .type("circle")
    //       .size(150)()
    //   )
    //   .shapePadding(10)
    //   .scale(categoryScale);

    //svg.select(".legendOrdinal").call(legendOrdinal);
  }
}

ScatterPlot.propTypes = {
  data: PropTypes.array.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  getX: PropTypes.func.isRequired,
  getY: PropTypes.func.isRequired,
  getRadius: PropTypes.func.isRequired,
  getKey: PropTypes.func.isRequired,
  getName: PropTypes.func.isRequired,
  getCategoryName: PropTypes.func.isRequired,
  xLabel: PropTypes.string.isRequired,
  yLabel: PropTypes.string.isRequired,
  onDotSelected: PropTypes.func,
  onScopeChanged: PropTypes.func
};

export default ScatterPlot;



// WEBPACK FOOTER //
// ./src/charts/ScatterPlot.js